// GroupEvents Component - Events list for a group

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Plus,
  Check,
  HelpCircle,
  X,
  Search,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { respondToEvent, createEvent } from '../../services/groupsService';
import { getApprovedPlaces } from '../../services/placesService';
import type { GroupEvent, GroupEventCreateInput, EventPlace } from '../../types/groups';
import type { Place } from '../../types';
import { Timestamp } from '@firebase/firestore';
import { format, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';

interface GroupEventsProps {
  groupId: string;
  events: GroupEvent[];
  isMember: boolean;
  onViewPlace?: (placeId: string) => void;
}

export function GroupEvents({
  groupId,
  events,
  isMember,
  onViewPlace,
}: GroupEventsProps) {
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);

  return (
    <div className="space-y-4">
      {/* Create Event Button */}
      {isMember && (
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="w-full p-4 bg-white rounded-xl border border-gray-200 border-dashed hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors flex items-center justify-center gap-2 text-indigo-600"
        >
          <Plus className="w-5 h-5" />
          Event erstellen
        </button>
      )}

      {/* Create Event Form */}
      {showCreateForm && (
        <CreateEventForm
          groupId={groupId}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      {/* Events List */}
      {events.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine anstehenden Events</h3>
          <p className="text-gray-500">
            {isMember
              ? 'Erstelle das erste Event fuer diese Gruppe!'
              : 'Tritt der Gruppe bei, um Events zu sehen und zu erstellen.'}
          </p>
        </div>
      ) : (
        events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            groupId={groupId}
            currentUserId={user?.uid}
            currentUserName={user?.displayName || 'Anonym'}
            currentUserPhoto={user?.photoURL || undefined}
            onViewPlace={onViewPlace}
          />
        ))
      )}
    </div>
  );
}

// Event Card Component
interface EventCardProps {
  event: GroupEvent;
  groupId: string;
  currentUserId?: string;
  currentUserName: string;
  currentUserPhoto?: string;
  onViewPlace?: (placeId: string) => void;
}

function EventCard({
  event,
  groupId,
  currentUserId,
  currentUserName,
  currentUserPhoto,
  onViewPlace,
}: EventCardProps) {
  const [responding, setResponding] = useState(false);

  const startDate = event.startDate instanceof Timestamp
    ? event.startDate.toDate()
    : event.startDate;

  const endDate = event.endDate
    ? (event.endDate instanceof Timestamp ? event.endDate.toDate() : event.endDate)
    : null;

  const userResponse = event.attendees.find(a => a.userId === currentUserId)?.status;

  const handleRespond = async (status: 'going' | 'maybe' | 'not_going') => {
    if (!currentUserId) return;
    setResponding(true);
    try {
      await respondToEvent(
        groupId,
        event.id,
        currentUserId,
        currentUserName,
        status,
        currentUserPhoto
      );
    } catch (err) {
      console.error('Error responding to event:', err);
    } finally {
      setResponding(false);
    }
  };

  const goingAttendees = event.attendees.filter(a => a.status === 'going');

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Cover Image */}
      {event.coverImageUrl && (
        <img
          src={event.coverImageUrl}
          alt={event.title}
          className="w-full h-40 object-cover"
        />
      )}

      <div className="p-4">
        {/* Date Badge */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-14 bg-indigo-50 rounded-lg text-center py-2">
            <p className="text-xs text-indigo-600 uppercase font-medium">
              {format(startDate, 'MMM', { locale: de })}
            </p>
            <p className="text-2xl font-bold text-indigo-700">
              {format(startDate, 'd')}
            </p>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>

            {/* Time */}
            <div className="flex items-center gap-1 mt-1 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              {event.isAllDay ? (
                <span>Ganztaegig</span>
              ) : (
                <span>
                  {format(startDate, 'HH:mm', { locale: de })}
                  {endDate && !isSameDay(startDate, endDate) && (
                    <> - {format(endDate, 'HH:mm', { locale: de })}</>
                  )}
                </span>
              )}
            </div>

            {/* Locations - Multiple Places */}
            {event.places && event.places.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {event.places.map((place) => (
                  <button
                    key={place.placeId}
                    onClick={() => onViewPlace?.(place.placeId)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full text-sm hover:bg-indigo-100 transition-colors"
                  >
                    <MapPin className="w-3 h-3" />
                    {place.placeName}
                  </button>
                ))}
              </div>
            ) : (event.placeName || event.customLocation) && (
              <button
                onClick={() => event.placeId && onViewPlace?.(event.placeId)}
                className={`flex items-center gap-1 mt-1 text-sm ${
                  event.placeId
                    ? 'text-indigo-600 hover:text-indigo-700'
                    : 'text-gray-600'
                }`}
              >
                <MapPin className="w-4 h-4" />
                {event.placeName || event.customLocation}
              </button>
            )}
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <p className="mt-3 text-gray-600 text-sm line-clamp-2">{event.description}</p>
        )}

        {/* Attendees */}
        <div className="flex items-center gap-2 mt-4">
          <div className="flex -space-x-2">
            {goingAttendees.slice(0, 5).map((attendee) => (
              attendee.photoURL ? (
                <img
                  key={attendee.userId}
                  src={attendee.photoURL}
                  alt={attendee.displayName}
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
              ) : (
                <div
                  key={attendee.userId}
                  className="w-8 h-8 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center"
                >
                  <Users className="w-4 h-4 text-gray-400" />
                </div>
              )
            ))}
          </div>
          <span className="text-sm text-gray-600">
            {event.attendeeCount} {event.attendeeCount === 1 ? 'nimmt teil' : 'nehmen teil'}
            {event.maxAttendees && ` (max. ${event.maxAttendees})`}
          </span>
        </div>

        {/* Response Buttons */}
        {currentUserId && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => handleRespond('going')}
              disabled={responding}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                userResponse === 'going'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-green-100 hover:text-green-700'
              }`}
            >
              <Check className="w-4 h-4" />
              Dabei
            </button>
            <button
              onClick={() => handleRespond('maybe')}
              disabled={responding}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                userResponse === 'maybe'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-yellow-100 hover:text-yellow-700'
              }`}
            >
              <HelpCircle className="w-4 h-4" />
              Vielleicht
            </button>
            <button
              onClick={() => handleRespond('not_going')}
              disabled={responding}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                userResponse === 'not_going'
                  ? 'bg-gray-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <X className="w-4 h-4" />
              Nicht dabei
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Create Event Form
interface CreateEventFormProps {
  groupId: string;
  onClose: () => void;
}

function CreateEventForm({ groupId, onClose }: CreateEventFormProps) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Place picker state
  const [selectedPlaces, setSelectedPlaces] = useState<EventPlace[]>([]);
  const [placeSearch, setPlaceSearch] = useState('');
  const [placeResults, setPlaceResults] = useState<Place[]>([]);
  const [allPlaces, setAllPlaces] = useState<Place[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);

  // Load places on mount
  useEffect(() => {
    const loadPlaces = async () => {
      setLoadingPlaces(true);
      try {
        const places = await getApprovedPlaces();
        setAllPlaces(places);
      } catch (err) {
        console.error('Error loading places:', err);
      } finally {
        setLoadingPlaces(false);
      }
    };
    loadPlaces();
  }, []);

  // Filter places based on search
  useEffect(() => {
    if (placeSearch.trim().length < 2) {
      setPlaceResults([]);
      return;
    }
    const searchLower = placeSearch.toLowerCase();
    const filtered = allPlaces
      .filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        p.address?.toLowerCase().includes(searchLower) ||
        p.district?.toLowerCase().includes(searchLower)
      )
      .filter(p => !selectedPlaces.some(sp => sp.placeId === p.id))
      .slice(0, 5);
    setPlaceResults(filtered);
  }, [placeSearch, allPlaces, selectedPlaces]);

  const handleAddPlace = (place: Place) => {
    const eventPlace: EventPlace = {
      placeId: place.id,
      placeName: place.name,
      placeImageUrl: place.imageUrl,
      address: place.address,
    };
    setSelectedPlaces(prev => [...prev, eventPlace]);
    setPlaceSearch('');
    setShowPlaceDropdown(false);
  };

  const handleRemovePlace = (placeId: string) => {
    setSelectedPlaces(prev => prev.filter(p => p.placeId !== placeId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !title || !date) return;

    setSubmitting(true);
    try {
      const startDate = new Date(`${date}T${time || '00:00'}`);

      await createEvent(
        {
          groupId,
          title,
          description,
          startDate,
          isAllDay,
          places: selectedPlaces.length > 0 ? selectedPlaces : undefined,
          customLocation: customLocation || undefined,
        },
        user.uid,
        user.displayName || 'Anonym'
      );

      onClose();
    } catch (err) {
      console.error('Error creating event:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-4">Neues Event</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titel *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="z.B. Stammtisch im Hofbraeuhaus"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Beschreibung
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Worum geht es bei diesem Event?"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Datum *
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Uhrzeit
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={isAllDay}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isAllDay}
            onChange={(e) => setIsAllDay(e.target.checked)}
            className="w-4 h-4 text-indigo-500 rounded border-gray-300 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-700">Ganztaegiges Event</span>
        </label>

        {/* Place Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="w-4 h-4 inline mr-1" />
            GoodPlaces verknuepfen
          </label>

          {/* Selected Places Tags */}
          {selectedPlaces.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedPlaces.map((place) => (
                <span
                  key={place.placeId}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                >
                  {place.placeImageUrl && (
                    <img
                      src={place.placeImageUrl}
                      alt=""
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  )}
                  {place.placeName}
                  <button
                    type="button"
                    onClick={() => handleRemovePlace(place.placeId)}
                    className="ml-1 hover:text-indigo-900"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Place Search Input */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={placeSearch}
                onChange={(e) => {
                  setPlaceSearch(e.target.value);
                  setShowPlaceDropdown(true);
                }}
                onFocus={() => setShowPlaceDropdown(true)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="GoodPlace suchen..."
              />
              {loadingPlaces && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
            </div>

            {/* Place Search Results Dropdown */}
            {showPlaceDropdown && placeResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                {placeResults.map((place) => (
                  <button
                    key={place.id}
                    type="button"
                    onClick={() => handleAddPlace(place)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                  >
                    {place.imageUrl ? (
                      <img
                        src={place.imageUrl}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{place.name}</p>
                      {place.address && (
                        <p className="text-sm text-gray-500 truncate">{place.address}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Mindestens 2 Zeichen eingeben zum Suchen
          </p>
        </div>

        {/* Custom Location (fallback) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Oder: Eigene Ortsangabe
          </label>
          <input
            type="text"
            value={customLocation}
            onChange={(e) => setCustomLocation(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="z.B. Marienplatz, Muenchen"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={submitting || !title || !date}
            className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Erstelle...' : 'Event erstellen'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default GroupEvents;
